import { decodeHtmlEntities, generateHTML, generateJSON, JSONContent } from '@tiptap/core';
import { MarkdownManager } from '@tiptap/markdown';
import { DocumentSection } from '../_interfaces/document';
import { createNoteContentExtensions } from '../document/helpers/editor-extensions';

const tipTapEmptyParagraph = /<p>(?:<br class="ProseMirror-trailingBreak">)?<\/p>/g;

let markdownManager: MarkdownManager | null = null;

function getMarkdownManager(): MarkdownManager {
  if (!markdownManager) {
    markdownManager = new MarkdownManager({ extensions: createNoteContentExtensions() });
  }
  return markdownManager;
}

function replaceBreaksWithNewlines(text: string): string {
  return text.replace(/<br\s*\/?>/gi, '\n\n');
}

/**
 * TipTap turns every extra blank line into an empty paragraph, and has no typographic ellipsis on parse.
 * API responses write each line break as `<br>`, so blank-line runs and edges are handled in both forms.
 */
export function preprocessMarkdown(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\n[ \t]*(?:\n[ \t]*)+\n/g, '\n\n')
    .replace(/(?:<br\s*\/?>[ \t]*){3,}/gi, '<br><br>')
    .replace(/^(?:\s*<br\s*\/?>)+|(?:<br\s*\/?>\s*)+$/gi, '')
    .replace(/\.{3}/g, '…')
    .trim();
}

function normalizeTitle(title: string): string {
  return title.replace(/\s+/g, ' ').trim().toLowerCase();
}

function removeDuplicateTitle(doc: JSONContent, knownTitles: readonly string[]): JSONContent {
  const [first, ...rest] = doc.content ?? [];
  if (first?.type !== 'heading' || first.attrs?.['level'] !== 1) {
    return doc;
  }
  const heading = normalizeTitle((first.content ?? []).map(node => node.text ?? '').join(''));
  const isDuplicate = knownTitles.some(title => title && normalizeTitle(title) === heading);
  return isDuplicate ? { ...doc, content: rest } : doc;
}

export function fillEmptyParagraphsWithInvisibleCharacter(html: string): string {
  return html.replace(tipTapEmptyParagraph, '<p>‎ </p>');
}

/** `knownTitles`: a leading H1 that repeats one of these is dropped. */
export function parseMarkdownToHtml(text: string, knownTitles: readonly string[] = []): string {
  const json = getMarkdownManager().parse(replaceBreaksWithNewlines(preprocessMarkdown(text)));
  const html = generateHTML(removeDuplicateTitle(json, knownTitles), createNoteContentExtensions());
  return fillEmptyParagraphsWithInvisibleCharacter(html);
}

function isBlankParagraph(node: JSONContent): boolean {
  return node.type === 'paragraph'
    && (node.content ?? []).every(child =>
      child.type === 'hardBreak'
      || (child.type === 'text' && !(child.text ?? '').replace(/[\s\u200e\u00a0]/g, '')));
}

/**
 * Inverse of `parseMarkdownToHtml` over the API wire format, where every line becomes its own block:
 * one block per line, and an empty paragraph as a single blank line.
 */
export function serializeNoteToMarkdown(html: string): string {
  if (!html.trim()) {
    return '';
  }
  const doc: JSONContent = generateJSON(html, createNoteContentExtensions());
  const lines = (doc.content ?? []).map(node => isBlankParagraph(node)
    ? ''
    : decodeHtmlEntities(getMarkdownManager().serialize({ type: 'doc', content: [node] }).trim()));
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function convertNoteSections(
  sections: Record<string, DocumentSection>,
  documentTitle?: string,
): Record<string, DocumentSection> {
  const converted: Record<string, DocumentSection> = {};
  for (const [key, section] of Object.entries(sections)) {
    converted[key] = {
      ...section,
      text: parseMarkdownToHtml(section.text, [section.title, documentTitle ?? '']),
    };
  }

  return converted;
}
